import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Instagram, Facebook, Mail, MapPin, Clock, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { SITE } from "@/lib/seo";
import { trackLead } from "@/lib/analytics";
import { toast } from "sonner";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [cols, setCols] = useState([]);
  useEffect(() => {
    api.get("/collections").then((r) => setCols(r.data)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const { data } = await api.post("/newsletter", { email });
      trackLead("newsletter");
      toast.success(data.message);
      setEmail("");
    } catch { toast.error("Please enter a valid email."); }
  };

  return (
    <footer className="bg-[#1A1A1A] text-[#FFFFFF] mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="font-display text-3xl leading-none">Kurti Connection</div>
          <div className="overline text-[#B84A3B] mt-1">USA</div>
          <p className="text-sm text-[#B0B0A8] mt-4 leading-relaxed max-w-xs">
            Indian ethnic wear &amp; wedding attire, based in the USA. Shipping across the USA &amp; Canada.
          </p>
          <div className="flex gap-3 mt-5">
            <a href="https://instagram.com/kurticonnection_usa" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="w-9 h-9 border border-[#3a3a3a] flex items-center justify-center hover:bg-[#B84A3B] hover:border-[#B84A3B] transition-colors"><Instagram size={16} /></a>
            <a href="https://facebook.com/KurticonnectionUSA" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="w-9 h-9 border border-[#3a3a3a] flex items-center justify-center hover:bg-[#B84A3B] hover:border-[#B84A3B] transition-colors"><Facebook size={16} /></a>
            <a href="mailto:hello@kurticonnection.com" data-testid="footer-mail" className="w-9 h-9 border border-[#3a3a3a] flex items-center justify-center hover:bg-[#B84A3B] hover:border-[#B84A3B] transition-colors"><Mail size={16} /></a>
          </div>
        </div>

        <div>
          <h4 className="overline text-[#B0B0A8] mb-4">Shop</h4>
          <ul className="space-y-2.5 text-sm">
            {cols.slice(0, 6).map((c) => (
              <li key={c.slug}><Link to={`/shop?collection=${c.slug}`} className="text-[#DDDDD5] hover:text-[#B84A3B] transition-colors">{c.title}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="overline text-[#B0B0A8] mb-4">Help</h4>
          <ul className="space-y-2.5 text-sm text-[#DDDDD5]">
            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-[#B84A3B] transition-colors">Contact Us</Link></li>
            <li><a href="https://account.kurticonnectionusa.com" target="_blank" rel="noreferrer" data-testid="footer-track-order" className="hover:text-[#B84A3B] transition-colors">Track My Order</a></li>
            <li><Link to="/returns" data-testid="footer-returns" className="hover:text-[#B84A3B] transition-colors">Returns &amp; Exchanges</Link></li>
            <li><Link to="/shop-code" data-testid="footer-shop-code" className="hover:text-[#B84A3B] transition-colors">Shop by Video Code</Link></li>
            <li><Link to="/about" data-testid="footer-about" className="hover:text-[#B84A3B] transition-colors">About Us</Link></li>
            <li><Link to="/bridesmaid-dresses" data-testid="footer-bridesmaid" className="hover:text-[#B84A3B] transition-colors">Bridesmaid Bulk Orders</Link></li>
            <li><Link to="/policies/shipping-policy" data-testid="footer-shipping-policy" className="hover:text-[#B84A3B] transition-colors">Shipping Policy</Link></li>
            <li><Link to="/policies/refund-policy" data-testid="footer-refund-policy" className="hover:text-[#B84A3B] transition-colors">Return &amp; Refund Policy</Link></li>
            <li><Link to="/policies/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-[#B84A3B] transition-colors">Privacy Policy</Link></li>
            <li><Link to="/policies/terms-of-service" data-testid="footer-terms" className="hover:text-[#B84A3B] transition-colors">Terms of Service</Link></li>
            <li>Free U.S. shipping over $75</li>
          </ul>
        </div>

        <div>
          <h4 className="overline text-[#B0B0A8] mb-4">Join the Family</h4>
          <p className="text-sm text-[#DDDDD5] mb-4">Get 10% off your first order + early access to new drops.</p>
          <form onSubmit={submit} className="flex border border-[#3a3a3a]">
            <input data-testid="footer-newsletter-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
              className="flex-1 bg-transparent px-3 py-3 text-sm focus:outline-none placeholder:text-[#6E6E69]" />
            <button data-testid="footer-newsletter-submit" className="bg-[#B84A3B] px-4 text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#FFFFFF] hover:text-[#1A1A1A] transition-colors">Join</button>
          </form>
        </div>
      </div>

      <div className="border-t border-[#2a2a2a]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="footer-store-info">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-[#B84A3B] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="overline text-[#B0B0A8] mb-1.5">Visit Our Store</h4>
              <p className="text-sm text-[#DDDDD5] leading-relaxed" data-testid="footer-address">183 West Golf Rd,<br />Schaumburg, IL 60195</p>
              <a href={`tel:${SITE.phone}`} data-testid="footer-phone"
                className="inline-block mt-1.5 text-sm text-[#DDDDD5] hover:text-[#B84A3B] transition-colors">{SITE.phoneDisplay}</a>
              <a href="https://maps.google.com/?q=183+West+Golf+Rd,+Schaumburg,+IL+60195" target="_blank" rel="noreferrer"
                data-testid="footer-directions"
                className="inline-block mt-2 text-[0.65rem] font-mono tracking-[0.15em] uppercase text-[#B84A3B] border-b border-[#B84A3B] pb-0.5 hover:text-[#FFFFFF] hover:border-[#FFFFFF] transition-colors">
                Get directions
              </a>
              <a href={SITE.doordash} target="_blank" rel="noopener noreferrer"
                data-testid="footer-doordash"
                className="flex w-fit items-center gap-1.5 mt-3 text-[0.65rem] font-mono tracking-[0.15em] uppercase text-[#F0B429] border-b border-[#F0B429] pb-0.5 hover:text-[#FFFFFF] hover:border-[#FFFFFF] transition-colors">
                <Zap size={12} /> Same-day delivery via DoorDash
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-[#B84A3B] mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="overline text-[#B0B0A8] mb-1.5">Store Hours</h4>
              <div className="text-sm text-[#DDDDD5] space-y-0.5" data-testid="footer-hours">
                <p>Mon: 2:00 PM – 7:00 PM</p>
                <p>Tue – Fri: 11:30 AM – 7:00 PM</p>
                <p>Sat &amp; Sun: 11:30 AM – 6:00 PM</p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-10" data-testid="footer-map">
          <div className="overflow-hidden border border-[#2a2a2a]">
            <iframe
              title="Kurti Connection store location"
              src="https://www.google.com/maps?q=183+West+Golf+Rd,+Schaumburg,+IL+60195&output=embed"
              width="100%"
              height="280"
              style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-[#2a2a2a] py-6 text-center text-[0.68rem] font-mono tracking-wide text-[#6E6E69]">
        © {new Date().getFullYear()} Kurti Connection USA · A redesign concept
      </div>
    </footer>
  );
};
